/**
 * Query param that scopes a request to a list of sites: `filter={"stationId":[...]}`.
 *
 * Same wire format the partner geofence calls and the multi-site list pages send, so every
 * multi-site request reads the same way on the server. Empty ids are dropped. No sites gives no
 * param: the caller decides what "no site" means for its endpoint (usually all of the partner's
 * sites, or a single legacy `stationId`).
 *
 * The catalog list, filter-options and export calls use it too. Only the catalog import still
 * sends a single `stationId` (see `catalog.ts`).
 */
export const toStationIdFilterParam = (stationIds?: string[]): { filter?: string } => {
  const ids = (stationIds ?? []).filter(Boolean);

  return ids.length ? { filter: JSON.stringify({ stationId: ids }) } : {};
};

/**
 * Unique, non-empty site ids of a request. `stationIds` wins over `stationId` when it has any id.
 * Used by `toAnalyticsSiteParams` below.
 */
const collectSiteIds = (stationId?: string, stationIds?: string[]): string[] => {
  const ids = [...new Set((stationIds ?? []).filter(Boolean))];

  return ids.length ? ids : [stationId].filter((id): id is string => Boolean(id));
};

/**
 * Site scope for the tlog analytics endpoints (`withDateRange` calls, store transactions).
 *
 * What: every site, one or many, goes as `filter={"stationId":[...]}`; no site gives no param
 * (all of the partner's sites).
 * Why (measured in the browser on a 310-site partner): `dashboard-header` and `category-insights`
 * IGNORE the single `stationId=<id>` query param (three different sites returned the same
 * all-sites data). They honour `filter` (a one-site list for a site with no data returned 0; a
 * two-site list returned the sum). So a single site must use the list format as well.
 * Not for `basket-analysis` (honours `stationId=`, builds its own param) or `activity-metrics`
 * (see `toActivityMetricsSiteParams`).
 */
export const toAnalyticsSiteParams = (
  stationId?: string,
  stationIds?: string[],
): { filter?: string } => toStationIdFilterParam(collectSiteIds(stationId, stationIds));

/**
 * Site scope for `station/activity-metrics` (Dashboard EV Transactions chart, EV Performance).
 *
 * What: a site list (`stationIds`, the Dashboard's picked sites) goes as
 * `filter={"stationId":[...]}` for ANY number of sites, one included, like the analytics calls.
 * A single `stationId` with no list (Site Performance, a site's EV Performance view) keeps the
 * legacy `stationId=<id>`. Neither gives no param (all of the partner's sites).
 * Backend dependency (measured 2026-10-08 on a 310-site partner): the endpoint then IGNORED
 * `filter` for one site and for several (returned all-sites totals) and honoured only
 * `stationId=`. The backend is adding `filter` support; until it ships, the Dashboard chart shows
 * all-sites data whenever a site list is sent. `stationId=` must keep working for EV Performance.
 */
export const toActivityMetricsSiteParams = (
  stationId?: string,
  stationIds?: string[],
): { filter?: string; stationId?: string } => {
  const ids = [...new Set((stationIds ?? []).filter(Boolean))];

  if (ids.length) {
    return toStationIdFilterParam(ids);
  }

  return stationId ? { stationId } : {};
};
